import { objectEntries } from '../../../utils/object/object-entries';
import { StorageAdapter } from './adapter-interface';
import { Build } from '@stencil/core';

export type CookieOptions = {
    domain?: string;
    path?: string;
    maxAge?: number;
    secure?: boolean;
    sameSite?: 'lax' | 'strict' | 'none';
};
export type Cookie = {
    value: string;
    options?: CookieOptions;
};

export class CookieAdapter implements StorageAdapter<Cookie> {
    getAll(): { [key: string]: Cookie } {
        return document.cookie.split(';').reduce((o, pair) => {
            if (pair) {
                const [key, value] = pair.split('=');
                return { ...o, [key?.trim()]: { value: value?.trim() } };
            } else return o;
        }, {});
    }
    get(key: string): Cookie {
        return {
            value: document.cookie
                .split(';')
                .find((row) => row.trim().startsWith(`${key}=`))
                ?.split('=')[1]
        };
    }
    set(name: string, cookie: Cookie) {
        const { value, options } = cookie;
        const cookieOptions = objectEntries<CookieOptions>(options).reduce(
            (str, [key, value]) => `${str};${valueToString(key, value)}`,
            ''
        );
        document.cookie = `${name}=${value}${cookieOptions}`;
    }
    has(key: string): boolean {
        const cookies = this.getAll();
        return key in cookies;
    }
    clear(): void {
        if (Build.isDev) {
            console.warn('Clearing all cookies is not supported');
        }
    }
    delete(name: string): void {
        document.cookie = `${name}=;expires=0;max-age=0`;
    }
}

const valueToString = (key: keyof CookieOptions, value: unknown): string => {
    switch (key) {
        case 'maxAge':
            return `max-age=${value}`;
        case 'sameSite':
            return `samesite=${value}`;
        case 'secure':
            return value ? 'secure' : '';
        default:
            return `${key.toLowerCase()}=${value}`;
    }
};
